import React, { memo, useCallback, useContext, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import * as R from 'ramda';

import { cloneDeep, isEmpty } from 'lodash';

import { setActiveLocation } from '@actions/data-detail-actions';

import ParamsContext from '@components/entity/info/params-context';
import SegmentEditListItem from '@components/forms/segment-edit-list-item';
import SegmentListItem from '@components/forms/segment-list-item';

import { detailEdit } from '@constants/mui-theme';

import { zipCoordinatePair } from '@utils/map-utils';

import './segment-list.scss';

const SegmentList = ({ readOnly }) => {
  const dispatch = useDispatch();
  const { dataType, publicConfig } = useContext(ParamsContext);
  const {
    activeLocation: { activeSegmentId },
    data,
    error: errors
  } = useSelector(state => state.dataDetail);
  const { segments = [] } = data;

  useEffect(() => {
    dispatch(setActiveLocation({ geometryLocked: readOnly }));
    if (readOnly) {
      dispatch(setActiveLocation({
        activeSegmentId: null,
        activeStart: null,
        activeEnd: null
      }));
    }
  }, [readOnly, dispatch]);

  const getSegmentErrors = useCallback(index => {
    const segmentErrors = errors?.segments?.[index];

    if (segmentErrors && typeof segmentErrors === 'object' && Object.keys(segmentErrors).length > 0) {
      return segmentErrors;
    }

    return null;
  }, [errors?.segments]);

  const onSegmentSelected = useCallback(segmentId => {
    if (activeSegmentId !== segmentId) {
      dispatch(setActiveLocation({ geometryLocked: readOnly }));
      const segment = segments.find(seg => seg.id === segmentId);
      switch (segment && segment.shape && segment.shape.type) {
      case 'LineString':
        dispatch(setActiveLocation({
          activeSegmentId: segmentId,
          activeType: 'LineString',
          activeStart: zipCoordinatePair(segment.shape.coordinates[0]),
          activeEnd: zipCoordinatePair(segment.shape.coordinates[segment.shape.coordinates.length - 1])
        }));
        break;
      case 'Point':
        dispatch(setActiveLocation({
          activeSegmentId: segmentId,
          activeType: 'Point',
          activeStart: zipCoordinatePair(segment.shape.coordinates),
          activeEnd: null
        }));
        break;
      case 'Polygon':
        dispatch(setActiveLocation({
          activeSegmentId: segmentId,
          activeType: 'Polygon',
          activeStart: null,
          activeEnd: null
        }));
        break;
      default:
        dispatch(setActiveLocation({
          activeSegmentId: segmentId,
          activeType: 'LineString',
          activeStart: null,
          activeEnd: null
        }));
        break;
      }
    } else {
      dispatch(setActiveLocation({ activeSegmentId: null }));
    }
  }, [activeSegmentId, dispatch, readOnly, segments]);

  if (!segments) {
    return null;
  }

  return (
    <div styleName="segment-list" style={detailEdit.columnStyles.col100}>
      {segments.map((segment, index) => {
        const onSegmentSelectedLocal = () => onSegmentSelected(segment.id);
        // Clone the error object, since we'll modify it:
        const segmentErrors = cloneDeep(getSegmentErrors(index));
        let hasEmptyAddressError = false;
        // Catch the global segment error about empty addresses, and erase it,
        // since we must show an error for each one of those fields instead.
        if (segmentErrors &&
            segmentErrors.shape &&
            segmentErrors.shape.length > 0 &&
            segmentErrors.shape[0] === 'A segment address must use a real address, street name or cross street.') {
          hasEmptyAddressError = true;
          delete segmentErrors.shape;
        }
        const hasErrors = !isEmpty(segmentErrors);
        const activeStyleName = activeSegmentId === segment.id ? 'active' : '';
        const errorStyleName = hasErrors ? 'segment-list-item-container-error' : '';
        return (
          <div
            key={segment.id}
            styleName={`segment-list-item-container ${activeStyleName} ${errorStyleName}`}
          >
            {hasErrors &&
              <div styleName="segment-error">{[].concat(R.values(segmentErrors)).join(', ')}</div>
            }
            {activeSegmentId !== segment.id &&
              <SegmentListItem
                checkAddressErrors={hasEmptyAddressError}
                data={data}
                dataType={dataType}
                onClick={onSegmentSelectedLocal}
                segment={segment}
                publicConfig={publicConfig}
              />
            }
            {activeSegmentId === segment.id &&
              <SegmentEditListItem
                checkAddressErrors={hasEmptyAddressError}
                data={data}
                dataType={dataType}
                onClick={onSegmentSelectedLocal}
                publicConfig={publicConfig}
                readOnly={readOnly}
              />
            }
          </div>
        );
      })}
    </div>
  );
};

SegmentList.propTypes = {
  readOnly: PropTypes.bool
};

export default memo(SegmentList);
